import { useState, useEffect, useCallback, useRef } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import { useAuthenticationStore } from 'src/domain/stores/useAuthenticationStore';
import { useEventStore } from 'src/domain/stores/useEventStore';
import type { ApiEventSubmission } from 'src/core/models/api/ApiEventSubmission';

export type MySubmissionStatus = 'idle' | 'loading' | 'success' | 'not-found' | 'error';

interface UseMySubmissionResult {
	status: MySubmissionStatus;
	submission: ApiEventSubmission | null;
	reload: () => void;
}

/**
 * Loads the logged-in user's personal registration (QR + registration number) for one event.
 */
export function useMySubmission(eventIdentifier: string | undefined): UseMySubmissionResult {
	const isLoggedin = useAuthenticationStore((state) => state.isLoggedin);
	const [status, setStatus] = useState<MySubmissionStatus>('idle');
	const [submission, setSubmission] = useState<ApiEventSubmission | null>(null);
	const [reloadToken, setReloadToken] = useState(0);

	const reload = useCallback(() => setReloadToken((token) => token + 1), []);
	const hasFocusedRef = useRef(false);

	useEffect(() => {
		if (!isLoggedin || !eventIdentifier) {
			setStatus('idle');
			setSubmission(null);
			return;
		}

		// AbortController guards against a state update after unmount or after the event changed (rapid
		// navigation): the stale response resolves against an aborted signal and is ignored.
		const controller = new AbortController();
		setStatus('loading');

		useEventStore
			.getState()
			.getMySubmission({ eventIdentifier, signal: controller.signal })
			.then((data) => {
				if (controller.signal.aborted) return;
				setSubmission(data);
				setStatus('success');
			})
			.catch((error: unknown) => {
				if (controller.signal.aborted) return;
				const httpStatus = (error as { response?: { status?: number } })?.response?.status;
				// 404 = the user has no (non-cancelled) submission for this event: an expected state, not a failure.
				setSubmission(null);
				setStatus(httpStatus === 404 ? 'not-found' : 'error');
			});

		return () => controller.abort();
	}, [eventIdentifier, isLoggedin, reloadToken]);

	// Refetch when the screen regains focus, e.g.
	useFocusEffect(
		useCallback(() => {
			if (hasFocusedRef.current) {
				reload();
			}
			hasFocusedRef.current = true;
		}, [reload]),
	);

	return { status, submission, reload };
}

export default useMySubmission;
